import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  LabelList,
} from 'recharts';
import { Box, CircularProgress, Typography } from '@mui/material';
import { BarChart3, List } from 'lucide-react';

const ACADEMIC_YEAR = '2025-2026';
const GRADE_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D', 'F'];
const GRADE_COLORS = {
  A1: '#10b981', // Emerald 500
  A2: '#34d399', // Emerald 400
  B1: '#3b82f6', // Blue 500
  B2: '#60a5fa', // Blue 400
  C1: '#f59e0b', // Amber 500
  C2: '#fbbf24', // Amber 400
  D: '#f97316',  // Orange 500
  F: '#ef4444',  // Red 500
};

const GRADE_LABELS = {
  A1: 'Excellent (A+)',
  A2: 'Excellent (A)',
  B1: 'Good (B+)',
  B2: 'Good (B)',
  C1: 'Satisfactory (C+)',
  C2: 'Satisfactory (C)',
  D: 'Needs Improvement',
  F: 'Fail',
};

const formatClassName = (cls) => {
  if (!cls) return 'Unknown Class';
  return `${cls.className || cls.name || 'Class'} ${cls.sectionName || ''}`.trim();
};

const normalizeGrade = (grade, marksObtained, maxMarks) => {
  const rawGrade = String(grade || '').trim().toUpperCase();
  if (rawGrade === '-' || rawGrade === '') {
    if (marksObtained === null || marksObtained === undefined || marksObtained === '') {
      return null; // Skip ungraded
    }
  }

  const gradeMap = {
    'A+': 'A1',
    'A': 'A2',
    'B+': 'B1',
    'B': 'B2',
    'C+': 'C1',
    'C': 'C2',
    'D+': 'D',
    'D': 'D',
    'F': 'F',
  };

  if (gradeMap[rawGrade]) {
    return gradeMap[rawGrade];
  }

  if (GRADE_ORDER.includes(rawGrade)) {
    return rawGrade;
  }

  const marks = Number(marksObtained);
  const total = Number(maxMarks);
  if (isNaN(marks) || isNaN(total) || total <= 0) {
    return null;
  }

  const percent = (marks / total) * 100;

  if (percent >= 90) return 'A1';
  if (percent >= 80) return 'A2';
  if (percent >= 70) return 'B1';
  if (percent >= 60) return 'B2';
  if (percent >= 50) return 'C1';
  if (percent >= 40) return 'D';
  return 'F';
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="performance-tooltip-container">
        <div className="performance-tooltip-header" style={{ borderLeftColor: data.fill }}>
          <span className="performance-tooltip-badge" style={{ backgroundColor: data.fill }}>
            {data.grade}
          </span>
          <span className="performance-tooltip-title">
            {GRADE_LABELS[data.grade]}
          </span>
        </div>
        <div className="performance-tooltip-divider" />
        <div className="performance-tooltip-body">
          <div className="performance-tooltip-row">
            <span className="performance-tooltip-label">Students:</span>
            <span className="performance-tooltip-value">{data.students}</span>
          </div>
          <div className="performance-tooltip-row">
            <span className="performance-tooltip-label">Share:</span>
            <span className="performance-tooltip-value">{data.percentage}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const ClassPerformanceCard = ({ orgId }) => {
  const { axios: authAxios, token } = useAuth();
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [courseNameMap, setCourseNameMap] = useState({});
  const [assessmentNameMap, setAssessmentNameMap] = useState({});
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [allExams, setAllExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'list'

  useEffect(() => {
    let active = true;

    const fetchClasses = async () => {
      if (!orgId || !token) return;

      try {
        setLoadingClasses(true);
        setError(null);

        const response = await authAxios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
        const items = Array.isArray(response.data) ? response.data : response.data?.items || [];
        const normalizedClasses = items.map((item) => ({
          id: item.classId || item.id,
          label: formatClassName(item),
        })).filter((item) => item.id);

        if (!active) return;

        if (normalizedClasses.length > 0) {
          setClasses(normalizedClasses);
          setSelectedClassId((prev) => {
            if (prev && normalizedClasses.some((c) => c.id === prev)) {
              return prev;
            }
            return normalizedClasses[0].id;
          });
        } else {
          setClasses([]);
          setSelectedClassId('');
        }
      } catch (err) {
        console.error('Failed to load classes for class performance:', err);
        console.error('Error details:', err.response?.data || err.message);
        if (active) {
          setError('Failed to load class options');
        }
      } finally {
        if (active) {
          setLoadingClasses(false);
        }
      }
    };

    fetchClasses();
    return () => { active = false; };
  }, [orgId, token, authAxios]);

  useEffect(() => {
    let active = true;
    const fetchExams = async () => {
      if (!orgId || !token) return;
      try {
        setLoadingExams(true);
        const response = await authAxios.get(configData.RESOURCES_EVENTS, {
          params: {
            orgId,
            eventStartDate: '2025-10-05T00:00:00Z',
            eventEndDate: '2030-12-31T23:59:59Z',
            pageSize: 100,
          },
        });
        const examEvents = (response.data.items || [])
          .filter(event => {
            const name = (event.eventName || '').toLowerCase();
            const type = event.eventType || '';
            return (
              type === 'EXAM_TERM' ||
              type === 'EXAM' ||
              name.includes('exam') ||
              name.includes('test') ||
              name.includes('assessment') ||
              name.includes('term')
            );
          })
          .map(event => ({
            id: event.eventId,
            label: event.eventName?.trim() || 'Untitled Exam',
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        if (!active) return;
        setAllExams(examEvents);
        
        const names = {};
        examEvents.forEach(ex => { names[ex.id] = ex.label; });
        setAssessmentNameMap(prev => ({ ...prev, ...names }));

      } catch (err) {
        console.error('Failed to fetch exams for dashboard:', err);
        console.error('Error details:', err.response?.data || err.message);
      } finally {
        if (active) setLoadingExams(false);
      }
    };
    fetchExams();
    return () => { active = false; };
  }, [orgId, token, authAxios]);

  useEffect(() => {
    let active = true;

    const fetchPerformance = async () => {
      if (!selectedClassId || !token) return;

      try {
        setLoadingPerformance(true);
        setError(null);

        let items = [];
        if (selectedClassId === 'ALL') {
          const classIdsToFetch = classes
            .map(c => c.id)
            .filter(id => id && id !== 'ALL');

          if (classIdsToFetch.length > 0) {
            const promises = classIdsToFetch.map(classId =>
              authAxios.get(
                configData.get_resources_resources_student_performance_class(classId),
                {
                  params: {
                    academicYear: ACADEMIC_YEAR,
                    pageSize: 500,
                  },
                }
              ).then(res => res.data?.items || [])
               .catch(err => {
                 console.warn(`Failed to fetch performance for class ${classId}:`, err);
                 return [];
               })
            );
            const results = await Promise.all(promises);
            items = results.flat();
          }
        } else {
          const performanceResponse = await authAxios.get(
            configData.get_resources_resources_student_performance_class(selectedClassId),
            {
              params: {
                academicYear: ACADEMIC_YEAR,
                pageSize: 500,
              },
            }
          );
          items = performanceResponse.data?.items || [];
        }

        if (!active) return;

        setPerformanceRecords(items);

        const courseIds = new Set();
        const assessmentIds = new Set();

        items.forEach((record) => {
          (record.assessmentPerformanceList || []).forEach((entry) => {
            if (entry.courseId) courseIds.add(entry.courseId);
            if (entry.assessmentId) assessmentIds.add(entry.assessmentId);
          });
        });

        const [courseEntries, assessmentEntries] = await Promise.all([
          Promise.all(
            Array.from(courseIds).map(async (courseId) => {
              try {
                const courseResponse = await authAxios.get(configData.get_resources_resources_courses(courseId));
                const courseName =
                  courseResponse.data?.courseDetails?.courseName ||
                  courseResponse.data?.courseName ||
                  courseResponse.data?.name ||
                  'Unknown Subject';
                return [courseId, courseName];
              } catch (courseError) {
                console.warn(`Failed to resolve course ${courseId}:`, courseError);
                return [courseId, courseId];
              }
            })
          ),
          Promise.all(
            Array.from(assessmentIds).map(async (assessmentId) => {
              try {
                const assessmentResponse = await authAxios.get(
                  `${configData.RESOURCES_API}/assessment/${assessmentId}`
                );
                return [assessmentId, assessmentResponse.data?.assessmentName || assessmentId];
              } catch (assessmentError) {
                console.warn(`Failed to resolve assessment ${assessmentId}:`, assessmentError);
                return [assessmentId, assessmentId];
              }
            })
          ),
        ]);

        if (!active) return;

        setCourseNameMap(Object.fromEntries(courseEntries));
        setAssessmentNameMap(Object.fromEntries(assessmentEntries));
      } catch (err) {
        console.error('Failed to load class performance data:', err);
        console.error('Error details:', err.response?.data || err.message);
        if (active) {
          setPerformanceRecords([]);
          setError('Failed to load performance data');
        }
      } finally {
        if (active) {
          setLoadingPerformance(false);
        }
      }
    };

    fetchPerformance();

    return () => {
      active = false;
    };
  }, [selectedClassId, token, authAxios, classes]);

  const subjectOptions = useMemo(() => {
    const ids = new Set();
    performanceRecords.forEach((record) => {
      (record.assessmentPerformanceList || []).forEach((entry) => {
        if (entry.courseId) {
          ids.add(entry.courseId);
        }
      });
    });

    const list = Array.from(ids).map((courseId) => ({
      id: courseId,
      label: courseNameMap[courseId] || courseId,
    })).sort((a, b) => a.label.localeCompare(b.label));

    if (list.length > 0) {
      return [{ id: '', label: 'All Subjects' }, ...list];
    }
    return list;
  }, [performanceRecords, courseNameMap]);

  const assessmentOptions = useMemo(() => {
    let options = [];
    if (allExams.length > 0) {
      options = allExams;
    } else {
      const ids = new Set();
      performanceRecords.forEach((record) => {
        (record.assessmentPerformanceList || []).forEach((entry) => {
          if (entry.assessmentId) {
            ids.add(entry.assessmentId);
          }
        });
      });

      options = Array.from(ids).map((assessmentId) => ({
        id: assessmentId,
        label: assessmentNameMap[assessmentId] || assessmentId,
      })).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (options.length > 0) {
      return [{ id: '', label: 'All Exams' }, ...options];
    }
    return options;
  }, [allExams, performanceRecords, assessmentNameMap]);

  useEffect(() => {
    if (!subjectOptions.length) {
      setSelectedSubjectId('');
      return;
    }

    setSelectedSubjectId((prev) => (
      subjectOptions.some((option) => option.id === prev) ? prev : ''
    ));
  }, [subjectOptions]);

  useEffect(() => {
    if (!assessmentOptions.length) {
      setSelectedAssessmentId('');
      return;
    }

    setSelectedAssessmentId((prev) => (
      assessmentOptions.some((option) => option.id === prev) ? prev : ''
    ));
  }, [assessmentOptions]);

  const analytics = useMemo(() => {
    const counts = GRADE_ORDER.reduce((acc, grade) => ({ ...acc, [grade]: 0 }), {});
    let totalCount = 0;
    let passCount = 0;
    let topCount = 0;

    performanceRecords.forEach((record) => {
      (record.assessmentPerformanceList || []).forEach((entry) => {
        if (selectedSubjectId && entry.courseId !== selectedSubjectId) return;
        
        if (selectedAssessmentId) {
          const matchesEvent = record.eventId === selectedAssessmentId;
          const matchesAssessment = entry.assessmentId === selectedAssessmentId;
          if (!matchesEvent && !matchesAssessment) return;
        }

        const normalized = normalizeGrade(entry.gradeObtained, entry.marksObtained, entry.maxMarks);
        if (normalized && counts[normalized] !== undefined) {
          counts[normalized] += 1;
          totalCount += 1;
          if (normalized !== 'F') {
            passCount += 1;
          }
          if (normalized === 'A1' || normalized === 'A2') {
            topCount += 1;
          }
        }
      });
    });

    const chartData = GRADE_ORDER.map((grade) => {
      const studentsCount = counts[grade] || 0;
      const percentage = totalCount > 0 ? Math.round((studentsCount / totalCount) * 100) : 0;
      return {
        grade,
        students: studentsCount,
        percentage,
        fill: GRADE_COLORS[grade],
      };
    });

    const passRate = totalCount > 0 ? Math.round((passCount / totalCount) * 100) : 0;
    const topPercentage = totalCount > 0 ? Math.round((topCount / totalCount) * 100) : 0;

    return {
      chartData,
      totalCount,
      passRate,
      topPercentage,
    };
  }, [performanceRecords, selectedSubjectId, selectedAssessmentId]);

  const isLoading = loadingClasses || loadingPerformance || loadingExams;
  const hasFilterData = classes.length > 0 && subjectOptions.length > 0 && assessmentOptions.length > 0;

  return (
    <div className="dashboard-card class-performance-card">
      <div className="class-performance-filters">
        <label className="class-performance-filter">
          <span>Class</span>
          <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}>
            {classes.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="class-performance-filter">
          <span>Subject</span>
          <select value={selectedSubjectId} onChange={(e) => setSelectedSubjectId(e.target.value)}>
            {subjectOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="class-performance-filter">
          <span>Exam</span>
          <select value={selectedAssessmentId} onChange={(e) => setSelectedAssessmentId(e.target.value)}>
            {assessmentOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
          <CircularProgress size={30} />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
          <Typography color="textSecondary" variant="body2">{error}</Typography>
        </Box>
      ) : !hasFilterData ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
          <Typography color="textSecondary" variant="body2">No class performance data available</Typography>
        </Box>
      ) : (
        <>
          <div className="performance-view-controls">
            <span className="performance-subtitle">Grade Distribution</span>
            <div className="performance-toggle-buttons">
              <button
                className={`toggle-btn ${viewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setViewMode('chart')}
              >
                <BarChart3 size={15} />
                <span>Chart</span>
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <List size={15} />
                <span>Breakdown</span>
              </button>
            </div>
          </div>

          <div className="class-performance-chart-wrap">
            {analytics.totalCount === 0 ? (
              <div className="class-performance-empty">
                No student grades found for the selected filters.
              </div>
            ) : viewMode === 'chart' ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.chartData} margin={{ top: 20, right: 12, left: -20, bottom: 8 }}>
                  <defs>
                    {analytics.chartData.map((entry) => (
                      <linearGradient id={`grad-${entry.grade}`} x1="0" y1="0" x2="0" y2="1" key={entry.grade}>
                        <stop offset="0%" stopColor={entry.fill} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={entry.fill} stopOpacity={0.65} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="grade"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 13, fontWeight: 600 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 600 }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(148, 163, 184, 0.04)' }}
                    content={<CustomTooltip />}
                  />
                  <Bar dataKey="students" radius={[6, 6, 0, 0]} barSize={38}>
                    {analytics.chartData.map((entry) => (
                      <Cell key={entry.grade} fill={`url(#grad-${entry.grade})`} />
                    ))}
                    <LabelList
                      dataKey="students"
                      position="top"
                      offset={6}
                      style={{ fill: '#475569', fontSize: 12, fontWeight: 700 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="performance-list-view">
                {analytics.chartData.map((item) => (
                  <div className="performance-list-item" key={item.grade}>
                    <div className="list-item-header">
                      <div className="grade-badge-wrap">
                        <span className="grade-dot" style={{ backgroundColor: item.fill }} />
                        <span className="grade-name">Grade {item.grade}</span>
                        <span className="grade-description">({GRADE_LABELS[item.grade]})</span>
                      </div>
                      <div className="grade-stats">
                        <span className="grade-count">{item.students} {item.students === 1 ? 'student' : 'students'}</span>
                        <span className="grade-pct">{item.percentage}%</span>
                      </div>
                    </div>
                    <div className="grade-progress-track">
                      <div
                        className="grade-progress-bar"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: item.fill,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ClassPerformanceCard;
